import { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { LoadingState } from '../../components/common/LoadingState';
import { useTranslation } from 'react-i18next';
import UserApiManager from '../../api/services/UserApiManager';
import { translateRole, translateStatus } from '../../i18n';
import { ThemeContext } from '../../context/ThemeContext';
import getDataTableStyles from '../../utils/tableStyles';

const DEFAULT_ROLES = [
  { id: 'SuperAdmin', name: 'Super Admin (Control General)' },
  { id: 'Admin', name: 'Administrador (Gestión BackOffice)' },
  { id: 'Supervisor', name: 'Supervisor (Comercial / Zona)' },
  { id: 'Vendedor', name: 'Vendedor (Campo / Visitas)' },
  { id: 'Mercaderista', name: 'Mercaderista (Chequeo de Mercado)' },
];

function Userlisting() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const customStyles = getDataTableStyles(isDark);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Catalogs state for dropdowns (inicializado con roles predeterminados)
  const [catalogs, setCatalogs] = useState({ roles: DEFAULT_ROLES, zones: [], supervisors: [] });
  const [catalogsLoading, setCatalogsLoading] = useState(false);

  // Validation & Error states
  const [validationError, setValidationError] = useState('');
  const [invalidFields, setInvalidFields] = useState({});
  const [successNotification, setSuccessNotification] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [originalEmail, setOriginalEmail] = useState('');
  const [sendInvitation, setSendInvitation] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [zoneImpact, setZoneImpact] = useState(null);
  const [zoneImpactLoading, setZoneImpactLoading] = useState(false);
  const [showImpactConfirmModal, setShowImpactConfirmModal] = useState(false);

  // Credential Modal state post-creation
  const [credentialModal, setCredentialModal] = useState({
    show: false,
    name: '',
    email: '',
    rol: '',
    tempPassword: '',
    copied: false
  });

  // Form state
  const initialForm = {
    nombre_completo: '',
    documento: '',
    email: '',
    password: '',
    rol: '',
    telefono: '',
    zone_id: '',
    manager_id: '',
    status: 'inactive'
  };
  const [formData, setFormData] = useState(initialForm);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getFilteredSupervisors = (role) => {
    if (role === 'SuperAdmin') {
      return [];
    }
    if (role === 'Admin') {
      return catalogs.supervisors.filter(s => s.rol === 'SuperAdmin');
    }
    if (role === 'Supervisor') {
      return catalogs.supervisors.filter(s => s.rol === 'Admin' || s.rol === 'SuperAdmin');
    }
    if (role === 'Vendedor' || role === 'Mercaderista') {
      return catalogs.supervisors.filter(s => s.rol === 'Supervisor' || s.rol === 'Admin' || s.rol === 'SuperAdmin');
    }
    return catalogs.supervisors;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await UserApiManager.getUsers({ 
        search, 
        rol: roleFilter, 
        status: statusFilter,
        per_page: 2500 
      });
      if (response && response.success) {
        const list = response.data?.data || response.data || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error cargando usuarios:', err);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  const loadCatalogs = async () => {
    setCatalogsLoading(true);
    try {
      const response = await UserApiManager.getEmployeeCatalogs();
      if (response.success && response.data) {
        const rawRoles = (response.data.roles && response.data.roles.length > 0) ? response.data.roles : DEFAULT_ROLES;
        const hasSupervisor = rawRoles.some(r => r.id === 'Supervisor');
        const roles = hasSupervisor 
          ? rawRoles 
          : [
              ...rawRoles.slice(0, 2),
              { id: 'Supervisor', name: 'Supervisor (Comercial / Zona)' },
              ...rawRoles.slice(2)
            ];

        setCatalogs({
          roles,
          zones: response.data.zones || [],
          supervisors: response.data.supervisors || []
        });
      }
    } catch (err) {
      console.error('Error cargando catálogos:', err);
    } finally {
      setCatalogsLoading(false);
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchZoneImpact = useCallback(async (userId, role, zoneId) => {
    const roleNorm = (role || '').toLowerCase();
    if (!['vendedor', 'mercaderista'].includes(roleNorm) || !zoneId) {
      setZoneImpact(null);
      return;
    }
    setZoneImpactLoading(true);
    try {
      const response = await UserApiManager.previewZoneImpact({
        user_id: userId || null,
        rol: role,
        zone_id: zoneId || null
      });
      if (response && response.success) {
        setZoneImpact(response.data);
      } else {
        setZoneImpact(null);
      }
    } catch (err) {
      console.error('Error calculando impacto de zona:', err);
      setZoneImpact(null);
    } finally {
      setZoneImpactLoading(false);
    }
  }, []);

  const handleOpenModal = (user = null) => {
    loadCatalogs();
    setValidationError('');
    setInvalidFields({});
    setZoneImpact(null);
    setShowImpactConfirmModal(false);

    if (user) {
      setEditingId(user.id);
      setOriginalEmail(user.email || '');
      setSendInvitation(false);
      setFormData({
        nombre_completo: user.nombre_completo || '',
        documento: user.documento || '',
        email: user.email || '',
        password: '',
        rol: user.rol || '',
        telefono: user.telefono || '',
        zone_id: user.zone_id || '',
        manager_id: user.manager_id || '',
        status: user.status || 'inactive'
      });
      const roleNorm = (user.rol || '').toLowerCase();
      if (['vendedor', 'mercaderista'].includes(roleNorm) && user.zone_id) {
        fetchZoneImpact(user.id, user.rol, user.zone_id);
      }
    } else {
      setEditingId(null);
      setOriginalEmail('');
      setSendInvitation(false);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const executeSaveUser = async () => {
    setProcessing(true);
    try {
      let response;
      if (editingId) {
        const payload = { ...formData };
        delete payload.password;
        if (sendInvitation) {
          payload.send_invitation = true;
        }
        const isFieldRole = ['vendedor', 'mercaderista'].includes((payload.rol || '').toLowerCase());
        if (!isFieldRole) payload.zone_id = null;
        response = await UserApiManager.updateUser(editingId, payload);

        const returnedData = response?.data || response?.data?.data;
        if (returnedData?.temp_password) {
          setCredentialModal({
            show: true,
            name: formData.nombre_completo,
            email: formData.email,
            rol: formData.rol,
            tempPassword: returnedData.temp_password,
            copied: false
          });
        } else {
          setSuccessNotification(response?.message || 'Usuario actualizado exitosamente.');
        }
      } else {
        const payload = { ...formData };
        const isFieldRole = ['vendedor', 'mercaderista'].includes((payload.rol || '').toLowerCase());
        if (!isFieldRole) payload.zone_id = null;
        response = await UserApiManager.createEmployee(payload);
        const createdUser = response?.data || response?.data?.data;
        const tempPass = createdUser?.temp_password || formData.password;
        setCredentialModal({
          show: true,
          name: formData.nombre_completo,
          email: formData.email,
          rol: formData.rol,
          tempPassword: tempPass,
          copied: false
        });
      }

      setFormData(initialForm);
      setInvalidFields({});
      setValidationError('');
      setZoneImpact(null);
      setShowImpactConfirmModal(false);
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        const fieldErrors = {};
        if (serverErrors.nombre_completo || serverErrors.first_name) fieldErrors.nombre_completo = true;
        if (serverErrors.email) fieldErrors.email = true;
        if (serverErrors.documento) fieldErrors.documento = true;
        if (serverErrors.password) fieldErrors.password = true;
        if (serverErrors.rol || serverErrors.role) fieldErrors.rol = true;

        setInvalidFields(fieldErrors);
        const errorMsg = Object.values(serverErrors).flat().join(' ');
        setValidationError(errorMsg || 'Todos los campos marcados son obligatorios');
      } else {
        setValidationError(err.response?.data?.message || 'Error guardando el usuario');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setValidationError('');
    setInvalidFields({});

    // Client-side validation for mandatory fields
    const errors = {};
    const isFieldRole = ['vendedor', 'mercaderista'].includes((formData.rol || '').toLowerCase());
    if (!formData.nombre_completo || !formData.nombre_completo.trim()) errors.nombre_completo = true;
    if (!formData.documento || !formData.documento.trim()) errors.documento = true;
    if (!formData.email || !formData.email.trim()) errors.email = true;
    if (!formData.telefono || !formData.telefono.trim()) errors.telefono = true;
    if (!formData.rol) errors.rol = true;
    if (isFieldRole && !formData.zone_id) errors.zone_id = true;
    if (formData.rol !== 'SuperAdmin' && !formData.manager_id) errors.manager_id = true;

    if (Object.keys(errors).length > 0) {
      setInvalidFields(errors);
      setValidationError('Todos los campos marcados son obligatorios');
      return;
    }

    const hasCriticalImpact = zoneImpact && !zoneImpact.no_change && (
      Boolean(zoneImpact.replaced_user) ||
      Boolean(zoneImpact.old_zone_left_without_titular) ||
      (zoneImpact.visits_today_in_progress > 0) ||
      (zoneImpact.visits_today_released > 0)
    );

    if (hasCriticalImpact) {
      setShowImpactConfirmModal(true);
      return;
    }

    await executeSaveUser();
  };

  const handleToggleStatus = async (user, overrideStatus = null) => {
    const isActivo = user.status?.toLowerCase() === 'active' || user.status?.toLowerCase() === 'activo';
    const newStatus = overrideStatus || (isActivo ? 'Desactivado' : 'Activo');
    const actionText = isActivo ? 'desactivar' : 'activar';
    
    if (!window.confirm(`¿Seguro que deseas ${actionText} a este usuario?`)) return;
    
    try {
      await UserApiManager.toggleUserStatus(user.id, newStatus);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error actualizando estado');
    }
  };

  const textColor = isDark ? '#f8fafc' : '#010028';
  const borderColor = isDark ? '#2d3748' : '#e2e8f0';
  const cardBg = isDark ? '#161e38' : '#ffffff';
  const isFieldRole = ['vendedor', 'mercaderista'].includes((formData.rol || '').toLowerCase());

  const columns = [
    {
      name: t('th_full_name', 'Nombre'),
      selector: row => row.nombre_completo,
      sortable: true,
      cell: row => <span className="fw-bold" style={{ color: textColor }}>{row.nombre_completo}</span>
    },
    {
      name: t('th_email', 'Email'),
      selector: row => row.email,
      sortable: true,
      cell: row => <span style={{ color: isDark ? '#cbd5e1' : 'inherit' }}>{row.email}</span>
    },
    {
      name: t('th_role', 'Rol'),
      selector: row => row.rol,
      sortable: true,
      cell: row => (
        <Badge bg={row.rol === 'Admin' || row.rol === 'SuperAdmin' ? 'danger' : row.rol === 'Vendedor' ? 'info' : 'secondary'}>
          {translateRole(row.rol, t)}
        </Badge>
      )
    },
    {
      name: t('th_status', 'Estado'),
      selector: row => row.status,
      sortable: true,
      cell: row => (
        <Badge bg={row.status?.toLowerCase() === 'active' || row.status?.toLowerCase() === 'activo' ? 'success' : 'secondary'}>
          {translateStatus(row.status, t)}
        </Badge>
      )
    },
    {
      name: t('th_actions', 'Acciones'),
      right: true,
      cell: row => (
        <div className="d-flex align-items-center justify-content-end gap-1">
          <button 
            className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" 
            style={{ width: '32px', height: '32px', padding: 0 }} 
            onClick={() => handleOpenModal(row)} 
            title={t('btn_edit', 'Editar')}
          >
            <i className="mdi mdi-pencil" style={{ fontSize: '1rem' }}></i>
          </button>
          {row.status?.toLowerCase() === 'active' || row.status?.toLowerCase() === 'activo' ? (
            <button 
              className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row, 'Desactivado')}
              title={t('btn_block', 'Bloquear')}
            >
              <i className="mdi mdi-account-off" style={{ fontSize: '1rem' }}></i>
            </button>
          ) : (
            <button 
              className="btn btn-sm btn-outline-success rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row, 'Activo')}
              title={t('btn_unblock', 'Activar')}
            >
              <i className="mdi mdi-account-check" style={{ fontSize: '1rem' }}></i>
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h3 className="page-title" style={{ color: textColor, fontWeight: 'bold' }}> {t('users_title', 'Gestión de Usuarios')} </h3>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>{t('menu_dashboard', 'Dashboard')}</a></li>
              <li className="breadcrumb-item active" aria-current="page">{t('menu_staff_roles', 'Personal y Roles')}</li>
            </ol>
          </nav>
        </div>
        <Button 
          style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }} 
          onClick={() => handleOpenModal()}
        >
          <i className="mdi mdi-account-plus me-1"></i> {t('btn_new_user', 'Nuevo Usuario')}
        </Button>
      </div>

      <div className="card shadow-sm" style={{ borderRadius: '12px', border: isDark ? '1px solid #2a3759' : 'none', backgroundColor: cardBg }}>
        <div className="card-body">
          {successNotification && (
            <div 
              className="mb-4 d-flex align-items-center justify-content-between p-3 shadow-sm"
              style={{ 
                borderRadius: '10px', 
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5', 
                border: isDark ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid #a7f3d0',
                borderLeft: '4px solid #10b981',
                color: isDark ? '#a7f3d0' : '#064e3b'
              }}
              role="alert"
            >
              <div className="d-flex align-items-center gap-2">
                <i className="mdi mdi-check-circle" style={{ fontSize: '1.25rem', color: isDark ? '#34d399' : '#10b981' }}></i>
                <span className="fw-semibold" style={{ fontSize: '0.88rem' }}>{successNotification}</span>
              </div>
              <button 
                type="button" 
                onClick={() => setSuccessNotification('')} 
                className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                style={{ 
                  width: '26px', 
                  height: '26px', 
                  borderRadius: '50%',
                  color: isDark ? '#a7f3d0' : '#064e3b',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  opacity: 0.75,
                  transition: 'opacity 0.2s ease, background-color 0.2s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.75'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label="Cerrar notificación"
              >
                <i className="mdi mdi-close" style={{ fontSize: '1.15rem' }}></i>
              </button>
            </div>
          )}

          <div className="row mb-4">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={{ color: textColor }}>{t('search_user_placeholder', 'Buscar Usuario')}</Form.Label>
                <Form.Control 
                  placeholder={t('search_user_placeholder', 'Nombre o email...')} 
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="customer-filter-control w-100 shadow-none"
                  style={{
                    backgroundColor: isDark ? '#1a223f' : '#ffffff',
                    color: textColor,
                    borderColor: borderColor
                  }}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={{ color: textColor }}>{t('th_role', 'Rol')}</Form.Label>
                <Form.Select 
                  value={roleFilter} 
                  onChange={e => setRoleFilter(e.target.value)}
                  className="customer-filter-control w-100 cursor-pointer shadow-none"
                  style={{
                    backgroundColor: isDark ? '#1a223f' : '#ffffff',
                    color: textColor,
                    borderColor: borderColor
                  }}
                >
                  <option value="">{t('status_all', 'Todos los Roles')}</option>
                  {catalogs.roles && catalogs.roles.map(r => (
                    <option key={r.id || r.name} value={r.id || r.name}>{translateRole(r.id || r.name, t)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={{ color: textColor }}>{t('th_status', 'Estado')}</Form.Label>
                <Form.Select 
                  value={statusFilter} 
                  onChange={e => setStatusFilter(e.target.value)}
                  className="customer-filter-control w-100 cursor-pointer shadow-none"
                  style={{
                    backgroundColor: isDark ? '#1a223f' : '#ffffff',
                    color: textColor,
                    borderColor: borderColor
                  }}
                >
                  <option value="">{t('status_all', 'Todos los Estados')}</option>
                  <option value="active">{t('status_active', 'Activo')}</option>
                  <option value="inactive">{t('status_inactive', 'Inactivo')}</option>
                  <option value="disabled">{t('status_disabled', 'Deshabilitado')}</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
            <div style={{ width: '100%' }}>
              <DataTable 
                columns={columns} 
                data={users} 
                pagination 
                customStyles={customStyles} 
                progressPending={loading}
                progressComponent={<LoadingState title={t('loading_users', 'Cargando usuarios del sistema...')} description={t('loading_users_sub', 'Consultando roles y permisos comerciales')} />}
                noDataComponent={<div className="my-5 text-muted text-center">{t('no_users_found', 'No se encontraron usuarios.')}</div>}
                paginationComponentOptions={{
                  rowsPerPageText: t('rows_per_page', 'Filas por página:'),
                  rangeSeparatorText: t('of_range', 'de'),
                  selectAllRowsItem: true,
                  selectAllRowsItemText: t('all_items', 'Todos')
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CREATE/EDIT MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered noValidate>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <Modal.Title>{editingId ? t('modal_edit_user', 'Editar Empleado y Permisos') : t('modal_new_user', 'Registrar Nuevo Empleado')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            {validationError && (
              <Alert variant="danger" className="mb-3 font-weight-bold d-flex align-items-center shadow-sm" style={{ borderRadius: '10px' }}>
                <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                <span>{validationError}</span>
              </Alert>
            )}

            {catalogsLoading ? (
              <div className="text-center py-4">
                <LoadingState size="sm" py="py-2" minHeight="60px" />
                <p className="mt-2 text-muted small">{t('loading_catalogs_server', 'Cargando catálogos del servidor...')}</p>
              </div>
            ) : (
              <>
                {/* SECCIÓN 1: DATOS PERSONALES */}
                <h6 className="fw-bold mb-3" style={{ color: '#183eeb', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                  <i className="mdi mdi-account-card-details me-2"></i>{t('section_personal_data', '1. Datos Personales del Empleado')}
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_full_name', 'Nombre Completo')} *</Form.Label>
                      <Form.Control 
                        type="text" 
                        isInvalid={!!invalidFields.nombre_completo}
                        value={formData.nombre_completo} 
                        onChange={e => {
                          setFormData({...formData, nombre_completo: e.target.value});
                          if (invalidFields.nombre_completo) setInvalidFields({...invalidFields, nombre_completo: false});
                        }} 
                        style={{ border: invalidFields.nombre_completo ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                        placeholder="Ej: Carlos Pérez" 
                      />
                      {invalidFields.nombre_completo && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          El nombre completo es obligatorio.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_document', 'Documento de Identidad')} *</Form.Label>
                      <Form.Control 
                        type="text" 
                        isInvalid={!!invalidFields.documento}
                        value={formData.documento} 
                        onChange={e => {
                          setFormData({...formData, documento: e.target.value});
                          if (invalidFields.documento) setInvalidFields({...invalidFields, documento: false});
                        }} 
                        style={{ border: invalidFields.documento ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                        placeholder="Ej: V-12345678" 
                      />
                      {invalidFields.documento && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          Documento de identidad obligatorio.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_email', 'Email (Usuario)')} *</Form.Label>
                      <Form.Control 
                        type="email" 
                        isInvalid={!!invalidFields.email}
                        value={formData.email} 
                        onChange={e => {
                          const newEmail = e.target.value;
                          setFormData({...formData, email: newEmail});
                          if (invalidFields.email) setInvalidFields({...invalidFields, email: false});
                          if (editingId && originalEmail && newEmail.trim().toLowerCase() !== originalEmail.trim().toLowerCase()) {
                            setSendInvitation(true);
                          }
                        }} 
                        style={{ border: invalidFields.email ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                        placeholder="correo@rutex.com" 
                      />
                      {invalidFields.email && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          Email obligatorio o inválido.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_phone', 'Teléfono')} *</Form.Label>
                      <Form.Control 
                        type="text" 
                        isInvalid={!!invalidFields.telefono}
                        value={formData.telefono} 
                        onChange={e => {
                          setFormData({...formData, telefono: e.target.value});
                          if (invalidFields.telefono) setInvalidFields({...invalidFields, telefono: false});
                        }} 
                        style={{ border: invalidFields.telefono ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }} 
                        placeholder="+584120000000" 
                      />
                      {invalidFields.telefono && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          Teléfono de contacto obligatorio.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                </div>

                {editingId ? (
                  <div 
                    className="mb-4 p-3 rounded border d-flex align-items-start gap-3" 
                    style={{ 
                      backgroundColor: formData.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase() ? '#eff6ff' : '#f8fafc', 
                      borderColor: formData.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase() ? '#93c5fd' : '#e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Form.Check 
                      type="checkbox"
                      id="send-invitation-checkbox"
                      checked={sendInvitation}
                      onChange={e => setSendInvitation(e.target.checked)}
                      className="mt-1"
                    />
                    <label htmlFor="send-invitation-checkbox" className="mb-0" style={{ cursor: 'pointer' }}>
                      <div className="fw-bold d-flex align-items-center gap-1" style={{ color: '#010028', fontSize: '0.88rem' }}>
                        <i className="mdi mdi-email-fast text-primary fs-5"></i>
                        <span>
                          {formData.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase() 
                            ? t('send_invitation_new_email', 'Enviar correo de invitación con contraseña temporal al nuevo correo') 
                            : t('send_invitation_resend', 'Generar nueva contraseña temporal y enviar correo de invitación')}
                        </span>
                      </div>
                      <div className="text-muted small mt-1">
                        {formData.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase()
                          ? t('send_invitation_new_email_desc', 'Se detectó un cambio en el correo electrónico. Al guardar con esta casilla marcada, el sistema generará una contraseña temporal de acceso y enviará la invitación por correo con su enlace de activación.')
                          : t('send_invitation_resend_desc', 'Marque esta casilla si desea generar una nueva clave temporal de acceso y despachar la invitación por correo electrónico.')}
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-light rounded border d-flex align-items-center" style={{ color: '#010028', borderRadius: '10px' }}>
                    <i className="mdi mdi-shield-key text-primary me-2" style={{ fontSize: '1.3rem' }}></i>
                    <small>
                      {t('temp_password_notice', 'La contraseña temporal de acceso se generará automáticamente y se enviará por correo al empleado al completar el registro.')}
                    </small>
                  </div>
                )}

                {/* SECCIÓN 2: ASIGNACIÓN OPERATIVA Y CATÁLOGOS */}
                <h6 className="fw-bold mb-3" style={{ color: '#183eeb', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                  <i className="mdi mdi-sitemap me-2"></i>{t('section_operational_assignment', '2. Asignación Operativa, Zona y Jerarquía')}
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_role', 'Rol de Acceso')} *</Form.Label>
                      <Form.Select 
                        isInvalid={!!invalidFields.rol}
                        value={formData.rol} 
                        onChange={e => {
                          const newRole = e.target.value;
                          let newManagerId = formData.manager_id;
                          if (newRole === 'SuperAdmin') {
                            newManagerId = '';
                          } else {
                            const validSupervisors = getFilteredSupervisors(newRole);
                            if (!validSupervisors.some(s => s.id === newManagerId)) {
                              newManagerId = '';
                            }
                          }
                          const isField = ['vendedor', 'mercaderista'].includes((newRole || '').toLowerCase());
                          const newZoneId = isField ? formData.zone_id : '';
                          setFormData(prev => ({ 
                            ...prev, 
                            rol: newRole, 
                            manager_id: newManagerId, 
                            zone_id: newZoneId 
                          }));
                          if (invalidFields.rol) setInvalidFields(prev => ({ ...prev, rol: false }));
                          if (invalidFields.manager_id) setInvalidFields(prev => ({ ...prev, manager_id: false }));
                          if (!isField && invalidFields.zone_id) setInvalidFields(prev => ({ ...prev, zone_id: false }));
                          if (isField && newZoneId) {
                            fetchZoneImpact(editingId, newRole, newZoneId);
                          } else {
                            setZoneImpact(null);
                          }
                        }} 
                        style={{ border: invalidFields.rol ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }}
                      >
                        <option value="">{t('select_role_placeholder', 'Seleccione un Rol')}</option>
                        {catalogs.roles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                      {invalidFields.rol && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          Debe seleccionar un rol de acceso.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>

                  {isFieldRole && (
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                          {t('field_zone', 'Zona Geográfica')} *
                        </Form.Label>
                        <Form.Select 
                          isInvalid={!!invalidFields.zone_id}
                          value={formData.zone_id || ''} 
                          onChange={e => {
                            const newZoneId = e.target.value;
                            setFormData(prev => ({ ...prev, zone_id: newZoneId }));
                            if (invalidFields.zone_id) setInvalidFields(prev => ({ ...prev, zone_id: false }));
                            if (newZoneId && isFieldRole) {
                              fetchZoneImpact(editingId, formData.rol, newZoneId);
                            } else {
                              setZoneImpact(null);
                            }
                          }} 
                          style={{ border: invalidFields.zone_id ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }}
                        >
                          <option value="">
                            {t('select_zone_placeholder', '-- Seleccione Zona Asignada --')}
                          </option>
                          {catalogs.zones.map(z => {
                            let tag = '';
                            const roleNorm = (formData.rol || '').toLowerCase();
                            if (roleNorm === 'vendedor') {
                              const isCurrentTitular = editingId && String(z.vendedor_id) === String(editingId);
                              const hasOtherTitular = z.vendedor_id && (!editingId || String(z.vendedor_id) !== String(editingId));
                              if (isCurrentTitular) {
                                tag = ' [SU ZONA ACTUAL]';
                              } else if (hasOtherTitular) {
                                const titularName = z.vendedor?.nombre_completo || 'Otro Vendedor';
                                tag = ` [TITULAR: ${titularName}]`;
                              } else {
                                tag = ' [DISPONIBLE]';
                              }
                            } else if (roleNorm === 'mercaderista') {
                              const isCurrentTitular = editingId && String(z.mercaderista_id) === String(editingId);
                              const hasOtherTitular = z.mercaderista_id && (!editingId || String(z.mercaderista_id) !== String(editingId));
                              if (isCurrentTitular) {
                                tag = ' [SU ZONA ACTUAL]';
                              } else if (hasOtherTitular) {
                                const titularName = z.mercaderista?.nombre_completo || 'Otro Mercaderista';
                                tag = ` [TITULAR: ${titularName}]`;
                              } else {
                                tag = ' [DISPONIBLE]';
                              }
                            }
                            return (
                              <option key={z.id} value={z.id}>
                                {z.name} ({z.code}){tag}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {invalidFields.zone_id && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            Debe seleccionar la zona de trabajo para roles de campo (Vendedor / Mercaderista).
                          </Form.Control.Feedback>
                        )}

                        {zoneImpactLoading && (
                          <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded" style={{ backgroundColor: '#f1f5f9', fontSize: '0.74rem', color: '#475569', borderRadius: '8px' }}>
                            <Spinner size="sm" animation="border" variant="primary" />
                            <span>Calculando impacto territorial y agendas en tiempo real...</span>
                          </div>
                        )}

                        {zoneImpact && !zoneImpactLoading && zoneImpact.applies && (
                          <>
                            {zoneImpact.no_change ? (
                              <div 
                                className="d-flex align-items-center gap-2 p-2 mt-2 rounded border"
                                style={{ 
                                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff', 
                                  borderColor: '#3b82f6', 
                                  color: isDark ? '#93c5fd' : '#1e40af',
                                  fontSize: '0.74rem',
                                  borderRadius: '8px'
                                }}
                              >
                                <i className="mdi mdi-information-outline fs-6 flex-shrink-0"></i>
                                <div>Zona actualmente asignada a este usuario. Sin cambios operativos.</div>
                              </div>
                            ) : (zoneImpact.replaced_user || zoneImpact.old_zone_left_without_titular || zoneImpact.visits_today_in_progress > 0 || zoneImpact.visits_today_released > 0) ? (
                              <div 
                                className="p-2 mt-2 rounded border"
                                style={{ 
                                  backgroundColor: isDark ? 'rgba(255, 59, 59, 0.12)' : '#fff1f2', 
                                  borderColor: '#ff3b3b', 
                                  color: isDark ? '#fca5a5' : '#991b1b',
                                  fontSize: '0.74rem',
                                  borderRadius: '8px'
                                }}
                              >
                                <div className="d-flex align-items-center gap-1 fw-bold mb-1" style={{ color: '#ff3b3b' }}>
                                  <i className="mdi mdi-alert-circle fs-6"></i>
                                  <span>Impacto Operativo y Cambio de Titularidad</span>
                                </div>
                                
                                {zoneImpact.replaced_user && (
                                  <div className="mb-1">
                                    <strong>Reemplazo de Titular:</strong> Se retirará la titularidad a <strong>{zoneImpact.replaced_user.nombre_completo}</strong> en esta zona.
                                  </div>
                                )}

                                {zoneImpact.old_zone_left_without_titular && (
                                  <div className="mb-1">
                                    <strong>Zona Descubierta:</strong> Su zona actual <strong>{zoneImpact.old_zone?.name} ({zoneImpact.old_zone?.code})</strong> quedará sin titular asignado.
                                  </div>
                                )}

                                {zoneImpact.visits_today_in_progress > 0 && (
                                  <div className="fw-bold mb-1" style={{ color: '#dc2626' }}>
                                    <i className="mdi mdi-clock-alert me-1"></i>¡Atención! Hay <strong>{zoneImpact.visits_today_in_progress}</strong> visita(s) con check-in en curso hoy en la zona anterior.
                                  </div>
                                )}

                                {zoneImpact.visits_today_released > 0 && (
                                  <div className="mb-1">
                                    <i className="mdi mdi-calendar-remove me-1"></i><strong>{zoneImpact.visits_today_released}</strong> visita(s) de hoy serán desasignadas.
                                  </div>
                                )}

                                {zoneImpact.visits_today_inherited > 0 && (
                                  <div className="mb-1">
                                    <i className="mdi mdi-calendar-check me-1"></i>Heredará <strong>{zoneImpact.visits_today_inherited}</strong> visita(s) de hoy en la nueva zona.
                                  </div>
                                )}

                                <div className="d-flex flex-wrap gap-1 mt-2 pt-1 border-top" style={{ borderColor: 'rgba(255, 59, 59, 0.2)' }}>
                                  {zoneImpact.customers_released > 0 && (
                                    <span className="badge" style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                                      <i className="mdi mdi-account-arrow-left me-1"></i>{zoneImpact.customers_released} clientes liberados
                                    </span>
                                  )}
                                  {zoneImpact.customers_inherited > 0 && (
                                    <span className="badge" style={{ backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #a5b4fc' }}>
                                      <i className="mdi mdi-account-arrow-right me-1"></i>{zoneImpact.customers_inherited} clientes a heredar
                                    </span>
                                  )}
                                  {zoneImpact.routes_released?.length > 0 && (
                                    <span className="badge" style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                                      <i className="mdi mdi-map-marker-remove me-1"></i>{zoneImpact.routes_released.length} ruta(s) liberadas
                                    </span>
                                  )}
                                  {zoneImpact.routes_inherited?.length > 0 && (
                                    <span className="badge" style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
                                      <i className="mdi mdi-map-marker-check me-1"></i>{zoneImpact.routes_inherited.length} ruta(s) a heredar
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div 
                                className="p-2 mt-2 rounded border"
                                style={{ 
                                  backgroundColor: isDark ? 'rgba(34, 197, 94, 0.12)' : '#f0fdf4', 
                                  borderColor: '#22c55e', 
                                  color: isDark ? '#86efac' : '#166534',
                                  fontSize: '0.74rem',
                                  borderRadius: '8px'
                                }}
                              >
                                <div className="d-flex align-items-center gap-1 fw-bold mb-1" style={{ color: '#16a34a' }}>
                                  <i className="mdi mdi-check-circle fs-6"></i>
                                  <span>Zona disponible sin conflictos</span>
                                </div>
                                <div>Asignación directa a {zoneImpact.new_zone?.name}.</div>
                                {(zoneImpact.customers_inherited > 0 || zoneImpact.routes_inherited?.length > 0) && (
                                  <div className="d-flex flex-wrap gap-1 mt-1 pt-1 border-top" style={{ borderColor: 'rgba(34, 197, 94, 0.2)' }}>
                                    {zoneImpact.customers_inherited > 0 && (
                                      <span className="badge" style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
                                        <i className="mdi mdi-account-multiple-check me-1"></i>{zoneImpact.customers_inherited} clientes
                                      </span>
                                    )}
                                    {zoneImpact.routes_inherited?.length > 0 && (
                                      <span className="badge" style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
                                        <i className="mdi mdi-map-marker-check me-1"></i>{zoneImpact.routes_inherited.length} ruta(s)
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </Form.Group>
                    </div>
                  )}

                  <div className={isFieldRole ? "col-md-12" : "col-md-6"}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>
                        {t('field_hierarchy_supervisor', 'Jerarquía / Supervisor')} {formData.rol === 'SuperAdmin' ? <small className="text-muted fw-normal">{t('not_applicable_superadmin', '(No aplica para SuperAdmin)')}</small> : '*'}
                      </Form.Label>
                      <Form.Select 
                        disabled={formData.rol === 'SuperAdmin' || !formData.rol}
                        isInvalid={!!invalidFields.manager_id}
                        value={formData.manager_id} 
                        onChange={e => {
                          setFormData(prev => ({ ...prev, manager_id: e.target.value }));
                          if (invalidFields.manager_id) setInvalidFields(prev => ({ ...prev, manager_id: false }));
                        }} 
                        style={{ border: invalidFields.manager_id ? '2px solid #dc3545' : '1px solid #183eeb', borderRadius: '10px' }}
                      >
                        {formData.rol === 'SuperAdmin' ? (
                          <option value="">{t('not_applicable_reports_to_none', '-- No aplica (SuperAdmin no reporta a nadie) --')}</option>
                        ) : !formData.rol ? (
                          <option value="">{t('select_role_first_placeholder', '-- Seleccione primero un Rol de Acceso --')}</option>
                        ) : (
                          <>
                            <option value="">
                              -- Seleccione {formData.rol === 'Admin' ? 'SuperAdmin a quien reporta' : 'Jefe o Administrador Directo'} --
                            </option>
                            {getFilteredSupervisors(formData.rol).map(s => (
                              <option key={s.id} value={s.id}>
                                {s.nombre_completo} — [{s.rol}] {s.email ? `(${s.email})` : ''}
                              </option>
                            ))}
                          </>
                        )}
                      </Form.Select>
                      {invalidFields.manager_id && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          Debe seleccionar el supervisor o jefe directo a quien reporta.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} style={{ color: '#010028', borderColor: '#010028', borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" disabled={processing} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : (editingId ? t('btn_save_changes', 'Guardar Cambios') : t('btn_create_user', 'Crear Usuario'))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal 
        show={showImpactConfirmModal} 
        onHide={() => setShowImpactConfirmModal(false)} 
        centered 
        size="lg"
        backdrop="static"
      >
        <Modal.Header 
          closeButton 
          closeVariant="white" 
          style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #ff3b3b' }}
        >
          <Modal.Title className="h5 fw-bold d-flex align-items-center">
            <i className="mdi mdi-alert-decagram me-2" style={{ color: '#ebfe52', fontSize: '1.4rem' }}></i>
            Confirmación de Reasignación Territorial y Operativa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: textColor }}>
          <p className="mb-3" style={{ fontSize: '0.9rem' }}>
            La asignación territorial seleccionada para <strong>{formData.nombre_completo || 'el usuario'}</strong> ({formData.rol}) genera modificaciones en la titularidad, agendas de visita y rutas comerciales activas. Revise los cambios antes de confirmar:
          </p>

          <div className="d-flex flex-column gap-3">
            {zoneImpact?.replaced_user && (
              <div 
                className="p-3 rounded border" 
                style={{ 
                  backgroundColor: isDark ? 'rgba(255, 59, 59, 0.12)' : '#fff1f2', 
                  borderColor: '#ff3b3b' 
                }}
              >
                <div className="d-flex align-items-center gap-2 fw-bold text-danger mb-1">
                  <i className="mdi mdi-account-arrow-left fs-5"></i>
                  <span>Reemplazo de Titular Territorial</span>
                </div>
                <div style={{ fontSize: '0.86rem' }}>
                  El usuario <strong>{zoneImpact.replaced_user.nombre_completo}</strong> será relevado como titular de <strong>{zoneImpact.new_zone?.name} ({zoneImpact.new_zone?.code})</strong>. Su asignación de zona será retirada y su cartera de clientes y rutas activas se transferirán a este usuario.
                </div>
              </div>
            )}

            {zoneImpact?.old_zone_left_without_titular && (
              <div 
                className="p-3 rounded border" 
                style={{ 
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.12)' : '#fffbeb', 
                  borderColor: '#f59e0b' 
                }}
              >
                <div className="d-flex align-items-center gap-2 fw-bold mb-1" style={{ color: isDark ? '#fbbf24' : '#b45309' }}>
                  <i className="mdi mdi-map-marker-alert fs-5"></i>
                  <span>Zona Anterior Quedará Acéfala (Sin Titular)</span>
                </div>
                <div style={{ fontSize: '0.86rem', color: isDark ? '#fde68a' : '#78350f' }}>
                  La zona <strong>{zoneImpact.old_zone?.name} ({zoneImpact.old_zone?.code})</strong> quedará sin {formData.rol?.toLowerCase()} titular asignado. Las rutas y clientes no compartidos de esa zona serán liberados.
                </div>
              </div>
            )}

            {(zoneImpact?.visits_today_in_progress > 0 || zoneImpact?.visits_today_released > 0 || zoneImpact?.visits_today_inherited > 0) && (
              <div 
                className="p-3 rounded border" 
                style={{ 
                  backgroundColor: isDark ? '#1a223f' : '#f8fafc', 
                  borderColor: isDark ? '#2a3759' : '#e2e8f0' 
                }}
              >
                <div className="d-flex align-items-center gap-2 fw-bold mb-2" style={{ color: '#183eeb' }}>
                  <i className="mdi mdi-calendar-clock fs-5"></i>
                  <span>Impacto en Visitas y Agendas del Día de Hoy</span>
                </div>
                <div className="d-flex flex-column gap-2" style={{ fontSize: '0.86rem' }}>
                  {zoneImpact.visits_today_in_progress > 0 && (
                    <div className="p-2 rounded border border-danger d-flex align-items-center gap-2" style={{ backgroundColor: isDark ? 'rgba(255, 59, 59, 0.2)' : '#fee2e2', color: '#991b1b' }}>
                      <i className="mdi mdi-alert-octagon-outline fs-5 text-danger flex-shrink-0"></i>
                      <span>
                        <strong>¡ALERTA CRÍTICA!</strong> Hay <strong>{zoneImpact.visits_today_in_progress}</strong> visita(s) con <strong>check-in activo en curso hoy</strong> en la zona anterior.
                      </span>
                    </div>
                  )}

                  {zoneImpact.visits_today_released > 0 && (
                    <div className="d-flex align-items-center gap-2">
                      <i className="mdi mdi-calendar-remove text-danger fs-6 flex-shrink-0"></i>
                      <span><strong>{zoneImpact.visits_today_released}</strong> visita(s) programadas para hoy en la zona anterior serán desasignadas del usuario.</span>
                    </div>
                  )}

                  {zoneImpact.visits_today_inherited > 0 && (
                    <div className="d-flex align-items-center gap-2">
                      <i className="mdi mdi-calendar-check text-success fs-6 flex-shrink-0"></i>
                      <span><strong>{zoneImpact.visits_today_inherited}</strong> visita(s) programadas para hoy en la nueva zona serán heredadas inmediatamente.</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div 
              className="p-3 rounded border" 
              style={{ 
                backgroundColor: isDark ? '#1a223f' : '#f8fafc', 
                borderColor: isDark ? '#2a3759' : '#e2e8f0' 
              }}
            >
              <div className="d-flex align-items-center gap-2 fw-bold mb-2" style={{ color: '#183eeb' }}>
                <i className="mdi mdi-briefcase-transfer fs-5"></i>
                <span>Transferencia de Cartera Comercial y Rutas</span>
              </div>
              <div className="row g-2" style={{ fontSize: '0.84rem' }}>
                <div className="col-sm-6">
                  <div className="p-2 rounded border bg-light h-100" style={{ color: '#010028' }}>
                    <div className="fw-bold text-success mb-1">
                      <i className="mdi mdi-arrow-down-bold me-1"></i>A Heredar en {zoneImpact?.new_zone?.name}:
                    </div>
                    <div>Clientes: <strong>{zoneImpact?.customers_inherited || 0}</strong></div>
                    <div>
                      Rutas ({zoneImpact?.routes_inherited?.length || 0}):{' '}
                      <span className="text-muted">
                        {zoneImpact?.routes_inherited?.length > 0 ? zoneImpact.routes_inherited.join(', ') : 'Ninguna'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="p-2 rounded border bg-light h-100" style={{ color: '#010028' }}>
                    <div className="fw-bold text-danger mb-1">
                      <i className="mdi mdi-arrow-up-bold me-1"></i>A Liberar de Zona Anterior:
                    </div>
                    <div>Clientes: <strong>{zoneImpact?.customers_released || 0}</strong></div>
                    <div>
                      Rutas ({zoneImpact?.routes_released?.length || 0}):{' '}
                      <span className="text-muted">
                        {zoneImpact?.routes_released?.length > 0 ? zoneImpact.routes_released.join(', ') : 'Ninguna'}
                      </span>
                    </div>
                    {zoneImpact?.tasks_released > 0 && (
                      <div>Tareas pendientes: <strong>{zoneImpact.tasks_released}</strong></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', borderTop: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowImpactConfirmModal(false)}
            style={{ borderRadius: '10px' }}
          >
            <i className="mdi mdi-arrow-left me-1"></i> Volver y Modificar
          </Button>
          <Button 
            disabled={processing}
            onClick={executeSaveUser}
            style={{ 
              backgroundColor: '#183eeb', 
              borderColor: '#183eeb', 
              color: '#ffffff', 
              borderRadius: '10px',
              fontWeight: 'bold' 
            }}
          >
            {processing ? (
              <>
                <Spinner size="sm" animation="border" className="me-1" />
                Guardando...
              </>
            ) : (
              <>
                <i className="mdi mdi-check-all me-1"></i>
                Entiendo el Impacto y Confirmo Asignación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SUCCESS CREDENTIALS & CLIPBOARD MODAL */}
      <Modal show={credentialModal.show} onHide={() => setCredentialModal(prev => ({ ...prev, show: false }))} centered size="md">
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff' }}>
          <Modal.Title className="h5 fw-bold">
            <i className="mdi mdi-account-key me-2" style={{ color: '#9fb2ff' }}></i>{t('credentials_created_title', 'Credenciales de Acceso Creadas')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#ffffff' }}>
          <Alert variant="success" className="d-flex align-items-center mb-3 border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <i className="mdi mdi-email-check me-2" style={{ fontSize: '1.4rem' }}></i>
            <div>
              <strong>{t('email_notification_sent', 'Notificación por correo enviada')}</strong>
              <div className="small">{t('email_notification_sent_desc', { email: credentialModal.email, defaultValue: `Se ha enviado un correo electrónico automático a ${credentialModal.email}.` })}</div>
            </div>
          </Alert>

          <div className="p-3 bg-light rounded-3 mb-3 border">
            <div className="mb-2"><strong>{t('employee_label', 'Empleado:')}</strong> {credentialModal.name}</div>
            <div className="mb-2"><strong>{t('th_role', 'Rol:')}</strong> <Badge bg="info">{translateRole(credentialModal.rol, t)}</Badge></div>
            <div className="mb-2"><strong>{t('th_email', 'Correo / Usuario:')}</strong> <code>{credentialModal.email}</code></div>
            <div className="mb-2 d-flex align-items-center">
              <strong className="me-2">{t('temp_password_label', 'Contraseña Temporal:')}</strong>
              <code className="fs-6 px-2 py-1 bg-white border rounded text-primary fw-bold me-2">{credentialModal.tempPassword}</code>
            </div>
          </div>

          <div className="d-grid gap-2">
            <Button 
              variant={credentialModal.copied ? "success" : "primary"}
              style={{ backgroundColor: credentialModal.copied ? '#10b981' : '#183eeb', borderColor: credentialModal.copied ? '#10b981' : '#183eeb', borderRadius: '10px' }}
              onClick={() => {
                const isMobile = ['Vendedor', 'Mercaderista'].includes(credentialModal.rol);
                const accessLink = isMobile ? 'https://rutex.com/download/app' : 'https://backoffice.dev.casmave.com/dashboard';
                const messageText = `Hola ${credentialModal.name}, bienvenido a Rutex.\n\nSe ha creado tu cuenta con el rol de ${credentialModal.rol}.\n\nTu usuario: ${credentialModal.email}\nContraseña temporal: ${credentialModal.tempPassword}\n\nAcceso a la plataforma: ${accessLink}\n\n*Nota: Al iniciar sesión por primera vez deberás cambiar tu contraseña por motivos de seguridad.`;
                
                navigator.clipboard.writeText(messageText);
                setCredentialModal(prev => ({ ...prev, copied: true }));
                setTimeout(() => setCredentialModal(prev => ({ ...prev, copied: false })), 3000);
              }}
            >
              <i className={credentialModal.copied ? "mdi mdi-check-all me-1" : "mdi mdi-content-copy me-1"}></i>
              {credentialModal.copied ? t('copied_to_clipboard', '¡Mensaje Copiado al Portapapeles!') : t('btn_copy_access_message', 'Copiar Mensaje de Acceso al Portapapeles')}
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCredentialModal(prev => ({ ...prev, show: false }))} style={{ borderRadius: '10px' }}>
            {t('btn_close', 'Cerrar')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Userlisting;